import {
  formatMoney,
  PAYMENT_MAX_CENTS,
  PAYMENT_MIN_CENTS,
  surchargeFor,
} from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useEffect, useReducer, useState } from 'react'
import { siteCarriesTimes } from '../lib/public-router'
import { holdsThePayment, returnedCheckout } from '../lib/returned-checkout'
import { readCivilDate, useToday } from '../lib/today'
import { useTRPC } from '../lib/trpc'
import { cardCostNote, continueLabel } from '../lib/two-step-payment'
import {
  Button,
  Field,
  MoneyInput,
  Notice,
  PaymentFlow,
  PaymentStep,
  PaymentThanks,
  ReturnedCheckoutNotice,
  SettingBlock,
  Statement,
  StatementPayByHand,
  TextInput,
  Toggle,
  useCheckoutConfirmation,
  useReturnedCheckout,
} from '../ui'
import { Challenge } from '../ui/Challenge'
import { PayAccount } from './PayAccount'
import { PayAutopay } from './PayAutopay'
import { PayMembership } from './PayMembership'
import './pay.css'
import {
  isKioskMode,
  KioskDoneOr,
  useKioskActivity,
  useKioskBusy,
  useKioskDone,
} from './kiosk'
import { initialKioskPayState, kioskPayReducer } from './kiosk-pay'

const money = formatMoney

function KioskPayPage() {
  const trpc = useTRPC()
  const shortId = window.location.pathname.split('/').filter(Boolean)[0] ?? ''
  const challenge = useQuery(trpc.turnstile.siteKey.queryOptions())
  const [flow, dispatch] = useReducer(kioskPayReducer, initialKioskPayState)
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [spentAt, setSpentAt] = useState(0)
  const activity = useKioskActivity(true)
  const request = useMutation({
    ...trpc.pay.kioskRequest.mutationOptions(),
    onSuccess: ({ requestId }) => dispatch({ type: 'requested', requestId }),
    onSettled: () => setSpentAt(Date.now()),
  })
  const verify = useMutation({
    ...trpc.pay.kioskVerify.mutationOptions(),
    onSuccess: ({ credential }) => dispatch({ type: 'verified', credential }),
  })
  const { mutate: touch } = useMutation(trpc.pay.kioskTouch.mutationOptions())
  const { address, code, requestId, credential } = flow

  useEffect(() => {
    if (!credential) return
    const timer = window.setInterval(() => touch({ credential }), 30_000)
    return () => window.clearInterval(timer)
  }, [credential, touch])

  useEffect(() => {
    if (!credential) return
    let ended = false
    const end = () => {
      if (ended) return
      ended = true
      void fetch('/api/trpc/pay.kioskEnd', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ json: { credential } }),
        keepalive: true,
      }).catch(() => {})
    }
    window.addEventListener('pagehide', end)
    return () => {
      window.removeEventListener('pagehide', end)
      end()
    }
  }, [credential])

  if (credential) {
    return (
      <AccountPayPage
        token={credential}
        kiosk
        onKioskExpired={() => dispatch({ type: 'restart' })}
      />
    )
  }
  return (
    <Shell>
      {requestId ? (
        <>
          <Notice>
            If that belongs to an account here, a code is on its way.
          </Notice>
          <Field label="Code">
            <TextInput
              inputMode="numeric"
              autoComplete="off"
              value={code}
              maxLength={6}
              onChange={(event) => {
                activity()
                dispatch({ type: 'code', value: event.target.value })
              }}
            />
          </Field>
          {verify.error ? (
            <Notice tone="error">{verify.error.message}</Notice>
          ) : null}
          <Button
            disabled={code.length !== 6 || verify.isPending}
            onClick={() => verify.mutate({ requestId, code })}
          >
            Open my account
          </Button>
        </>
      ) : (
        <>
          <Field label="Phone or email">
            <TextInput
              autoComplete="off"
              value={address}
              onChange={(event) => {
                activity()
                dispatch({ type: 'address', value: event.target.value })
              }}
            />
          </Field>
          <Challenge
            siteKey={challenge.data?.siteKey ?? null}
            onToken={setChallengeToken}
            spentAt={spentAt}
          />
          {request.error ? (
            <Notice tone="error">{request.error.message}</Notice>
          ) : null}
          <Button
            disabled={
              !address.trim() ||
              request.isPending ||
              challenge.isPending ||
              challenge.isError ||
              (Boolean(challenge.data?.siteKey) && challengeToken === null)
            }
            onClick={() =>
              request.mutate({
                shortId,
                address: address.trim(),
                challenge: challengeToken,
              })
            }
          >
            Send my code
          </Button>
        </>
      )}
    </Shell>
  )
}

/**
 * An account, from the link in its statement or the one View my account
 * mails: the statement, a way to pay it, and what is ongoing — a family's
 * account and a person's own alike.
 * No login: members hold no session, so the `?token=` in the URL is the whole
 * authorization and it scopes every read to that one account. A membership
 * link reaches the membership alone.
 *
 * The charges are shown rather than only the total, because somebody asked to
 * pay ought to be able to see what for — and the statement email that carried
 * them here already listed them, so the page discloses nothing the less
 * protected artifact did not.
 *
 * Nothing here records a payment. Stripe's webhook does that, so closing the tab
 * after submitting loses nothing.
 */
export function PayPage() {
  return isKioskMode() ? <KioskPayPage /> : <AccountPayPage />
}

function AccountPayPage({
  token: givenToken,
  kiosk = false,
  onKioskExpired,
}: {
  token?: string
  kiosk?: boolean
  onKioskExpired?: () => void
}) {
  const trpc = useTRPC()
  const params = new URLSearchParams(window.location.search)
  const token = givenToken ?? params.get('token') ?? ''
  const activity = useKioskActivity(kiosk)
  const kioskBusy = useKioskBusy(kiosk)
  const [completedCheckoutId, showCompletedCheckout] = useReturnedCheckout()
  /* What was paid, taken from the checkout as it completed. A return after a
     reload has none, and is thanked without a figure. */
  const [paidCents, setPaidCents] = useState<number | null>(null)
  const today = useToday()
  const confirmation = useCheckoutConfirmation({
    onComplete: (checkoutId) => {
      setPaidCents(checkoutTotalCents)
      showCompletedCheckout(checkoutId)
    },
  })

  const bill = useQuery({
    ...trpc.pay.bill.queryOptions({ token }),
    enabled: token.length > 0,
    retry: false,
  })
  /* Every account may hold a membership but a person's own at a shul, which
     is held by a family. Unknown until the bill answers, and a membership
     link is one the bill refuses. */
  const offersMembership = bill.data?.offersMembership ?? true
  const membership = useQuery({
    ...trpc.pay.membership.queryOptions({ token }),
    /* A membership link is one the bill refuses, so it is asked once the
       bill has answered either way — unless the bill says the account is a
       person's own. */
    enabled: token.length > 0 && !bill.isPending && offersMembership,
    retry: false,
  })
  const checkout = useMutation({
    ...trpc.pay.checkout.mutationOptions(),
  })
  const outcome = useQuery({
    ...trpc.donation.checkoutOutcome.queryOptions({
      shortId: bill.data?.orgShortId ?? '',
      checkoutId: completedCheckoutId ?? '',
    }),
    enabled: completedCheckoutId !== null && bill.data !== undefined,
  })

  const [custom, setCustom] = useState('')
  // Off by default: the payer opts in, having seen what it adds.
  const [coverFee, setCoverFee] = useState(false)
  const returned = returnedCheckout({
    checkoutId: completedCheckoutId,
    status: outcome.data?.status,
    isError: outcome.isError,
  })
  const paid = returned === 'paid'
  useKioskDone(kiosk, paid)
  useEffect(() => {
    if (paid || outcome.isError) kioskBusy.release()
  }, [kioskBusy, outcome.isError, paid])
  const startAgain = () => {
    showCompletedCheckout(null)
    setPaidCents(null)
    setCustom('')
    setCoverFee(false)
    checkout.reset()
    confirmation.reset()
  }
  const checkoutTotalCents = checkout.variables
    ? checkout.variables.amountCents +
      (checkout.variables.coverFee
        ? surchargeFor(checkout.variables.amountCents)
        : 0)
    : null

  if (!token) {
    return (
      <Shell>
        <Notice tone="error">
          This link is missing its token. Please use the link from the statement
          we sent you.
        </Notice>
      </Shell>
    )
  }
  /* A link is one kind or the other: the account link serves both reads, a
     membership link only the second — so the page waits for both answers
     before deciding which it is, or calling the link bad. */
  if (bill.isLoading || membership.isLoading) {
    return (
      <Shell>
        <p className="pay__muted">Loading your account…</p>
      </Shell>
    )
  }
  if (!bill.data && membership.data) {
    return (
      <Shell
        subtitle={`${membership.data.orgName} · ${membership.data.accountName}`}
      >
        <PayMembership
          token={token}
          view={membership.data}
          startEditing={params.get('edit') === '1'}
        />
      </Shell>
    )
  }
  if (!bill.data) {
    return (
      <Shell>
        <Notice tone="error">
          {bill.error?.message ??
            `This link is not valid. Please use the link from a recent statement, or contact ${siteCarriesTimes() ? 'the shul office' : 'the office'}.`}
        </Notice>
        {onKioskExpired ? (
          <Button variant="secondary" onClick={onKioskExpired}>
            Start again
          </Button>
        ) : null}
      </Shell>
    )
  }

  const b = bill.data
  const owed = b.netOwedCents
  /* The balance is the default, because paying it in full is what the link is
     for; a smaller amount is typed rather than chosen from presets, since the
     only figures that mean anything here are this household's own. With
     nothing owed there is no figure to suggest, and the amount is typed. */
  const amountCents = custom.trim()
    ? Math.round(Number(custom) * 100)
    : Math.max(owed, 0)
  /* More than the balance is allowed — paying ahead is a real thing, and the
     excess becomes credit on the account. The ceiling is a typo guard. */
  const valid =
    Number.isFinite(amountCents) &&
    amountCents >= PAYMENT_MIN_CENTS &&
    amountCents <= PAYMENT_MAX_CENTS
  const feeCents = valid && coverFee ? surchargeFor(amountCents) : 0
  const chargedCents = amountCents + feeCents
  const balance = <Statement view={b} />

  return (
    <Shell subtitle={`${b.orgName} · ${b.accountName}`}>
      <ReturnedCheckoutNotice state={returned} />

      {paid ? (
        <>
          {/* The outcome is read from Stripe by the server. The webhook remains
              the ledger writer and may land a moment later, so the balance can
              trail — which is what the line under the thanks says. */}
          <PaymentThanks
            thanks={{
              headline:
                paidCents !== null
                  ? `Thank you for your payment of ${money(paidCents)}.`
                  : 'Thank you — your payment went through.',
              receipt:
                'It can take a moment to show on the balance below. There is nothing else for you to do.',
              record: [
                { label: 'For', value: `${b.accountName}, ${b.orgName}` },
                ...(paidCents !== null
                  ? [{ label: 'Charged', value: money(paidCents) }]
                  : []),
                { label: 'Date', value: readCivilDate(today, 'long') },
              ],
            }}
          >
            <KioskDoneOr kiosk={kiosk}>
              <Button variant="secondary" onClick={startAgain}>
                Make another payment
              </Button>
            </KioskDoneOr>
          </PaymentThanks>
          {balance}
        </>
      ) : (
        <>
          {balance}

          {b.acceptsPayments ? (
            <PaymentFlow hidden={holdsThePayment(returned)}>
              <Field
                label={
                  owed > 0 ? 'Amount to pay' : 'Amount to add to my account'
                }
              >
                <MoneyInput
                  placeholder={owed > 0 ? (owed / 100).toFixed(2) : '0.00'}
                  value={custom}
                  onChange={(e) => {
                    activity()
                    setCustom(e.target.value)
                  }}
                />
              </Field>
              {custom.trim() && !valid && (
                <Notice tone="error">Enter an amount of $1.00 or more.</Notice>
              )}
              {owed > 0 && valid && amountCents > owed && (
                <Notice>
                  That is {money(amountCents - owed)} more than the balance. The
                  extra stays on the account as credit.
                </Notice>
              )}

              {/* A setting, not a field: it changes what the charge IS, and it
                  takes effect as it is flipped. Off by default — the payer opts
                  in, having seen what it adds. */}
              <SettingBlock
                title="Cover the card processing cost"
                description={
                  valid
                    ? `Card processing is charged on every payment. Adds ${money(surchargeFor(amountCents))}, so ${b.giftTo} receives the full ${money(amountCents)}.`
                    : `Card processing is charged on every payment. This adds it, so ${b.giftTo} receives the full amount.`
                }
              >
                <Toggle
                  checked={coverFee}
                  onChange={setCoverFee}
                  label="Cover the card processing cost"
                  hideLabel
                />
              </SettingBlock>

              {checkout.error && (
                <Notice tone="error">{checkout.error.message}</Notice>
              )}
              <Button
                onClick={() => {
                  if (valid) checkout.mutate({ token, amountCents, coverFee })
                }}
                disabled={!valid || checkout.isPending}
              >
                {continueLabel(checkout.isPending)}
              </Button>
              <p className="pay__fine">
                Next, your card. Nothing is charged yet.
              </p>
              {checkout.data && checkout.variables && (
                <PaymentStep
                  orgId={b.orgShortId}
                  card={checkout.data.card}
                  what={
                    owed > 0
                      ? `Toward your balance of ${money(owed)}`
                      : 'Added to your account'
                  }
                  who={b.accountName}
                  amount={money(checkoutTotalCents ?? chargedCents)}
                  amountNote={cardCostNote(
                    (checkoutTotalCents ?? chargedCents) -
                      checkout.variables.amountCents,
                  )}
                  payLabel={`Pay ${money(checkoutTotalCents ?? chargedCents)}`}
                  confirmation={confirmation}
                  onConfirmingChange={kioskBusy.setBusy}
                  onCardChange={activity}
                  onBack={() => {
                    checkout.reset()
                    confirmation.reset()
                  }}
                />
              )}
            </PaymentFlow>
          ) : (
            <Notice tone="info">
              {/* Where the ways to pay by hand follow, they are the answer;
                  the office is the answer only where nothing does. */}
              {siteCarriesTimes() ? 'This shul' : b.orgName} isn’t set up to
              take payments online yet.
              {b.payByHand.length ? null : ' Please contact the office.'}
            </Notice>
          )}
          {b.payByHand.length ? (
            <div className="pay__by-hand">
              <StatementPayByHand ways={b.payByHand} />
            </div>
          ) : null}
        </>
      )}
      {/* Below the bill, because paying what is owed is what the link is for and
          what goes on happening is what a member reads having done that. */}
      <PayAccount
        token={token}
        membership={membership.data}
        orgShortId={b.orgShortId}
        who={b.accountName}
        onCardChange={activity}
        onConfirmingChange={kioskBusy.setBusy}
      />
      {/* The membership's own section turns its automatic payments on and
          off, so it is not offered a second time here. */}
      <PayAutopay
        token={token}
        justPaid={outcome.data?.status === 'paid'}
        leaving={membership.data?.arrangement?.id ?? null}
      />
    </Shell>
  )
}

function Shell({
  children,
  subtitle,
}: {
  children: ReactNode
  subtitle?: string
}) {
  return (
    <div className="pay">
      <div className="pay__card">
        <h1 className="pay__title">Your account</h1>
        {subtitle && <p className="pay__sub">{subtitle}</p>}
        {children}
      </div>
    </div>
  )
}
